import React, { useEffect, useState } from 'react';
import { Card } from '../../common/Card';
import { Text } from '../../common/Typography';
import ClubTournamentList from '../../tournaments/ClubTournamentList';
import { ClubSessionsOverviewCarousel } from './ClubSessionsOverviewCarousel';
import { fetchClubPlayerSessionWinRates } from '../../../services/clubDashboardService';

/**
 * Single KPI tile — matches the stat tile pattern used across the new dashboard.
 */
function KpiTile({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-surface-subtle p-3 flex flex-col gap-1.5 min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted truncate">{label}</p>
      <span className="text-2xl font-bold tabular-nums text-text-primary leading-none">
        {value ?? '—'}
      </span>
    </div>
  );
}

/**
 * Combined "Club" tab — the club-facing, discovery-oriented view.
 *
 * Three sections in one place:
 *   1. About — club stats, description, contact
 *   2. Sessions — upcoming + past
 *   3. Tournaments
 *
 * Distinct from "My Club" (overview) which is player-journey focused.
 * This tab answers: "What is this club? What's on? What's coming up?"
 */
export function ClubScheduleTab({
  club,
  clubStats,
  userMembership,
  getRoleDisplayName,
  recentSessions,
  upcomingSessions,
  currentUser,
  onViewSessionDetails,
  onViewSessionPerformance,
  clubId,
  canManage,
  onRefresh,
  selectedSeason,
  seasons,
  setSelectedSeason,
}) {
  const [sessionWinRates, setSessionWinRates] = useState(new Map());

  const recentSessionKey = (recentSessions || []).map((session) => session.id).filter(Boolean).join(',');

  useEffect(() => {
    if (!currentUser?.id || !clubId) return;
    let cancelled = false;

    const sessionIds = recentSessionKey ? recentSessionKey.split(',') : [];
    fetchClubPlayerSessionWinRates(currentUser.id, clubId, sessionIds)
      .then((rates) => { if (!cancelled) setSessionWinRates(rates); })
      .catch(() => { if (!cancelled) setSessionWinRates(new Map()); });

    return () => { cancelled = true; };
  }, [currentUser?.id, clubId, recentSessionKey]);

  const months =
    club?.created_at != null
      ? Math.floor((Date.now() - new Date(club.created_at).getTime()) / (1000 * 60 * 60 * 24 * 30))
      : null;

  const roleLabel = userMembership ? getRoleDisplayName(userMembership.club_role) : null;

  const kpiTiles = [
    { key: 'players',  label: 'Players',        value: clubStats?.clubPlayersCount ?? 0 },
    { key: 'sessions', label: 'Total sessions',  value: clubStats?.finishedSessions ?? 0 },
    { key: 'upcoming', label: 'Upcoming',        value: clubStats?.upcomingSessionsCount ?? 0 },
    { key: 'age',      label: 'Club age',        value: months != null ? `${months} mo` : '—' },
    ...(roleLabel ? [{ key: 'role', label: 'Your role', value: roleLabel }] : []),
  ];

  return (
    <div className="space-y-8">

      {/* ── 1. About ──────────────────────────────────────────── */}
      <Card variant="default">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-4">
          About
        </p>

        {/* KPI tiles grid */}
        <div className={`grid gap-3 mb-5 ${roleLabel ? 'grid-cols-3 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4'}`}>
          {kpiTiles.map((tile) => (
            <KpiTile key={tile.key} label={tile.label} value={tile.value} />
          ))}
        </div>

        {/* Description + Contact */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-4 border-t border-border-subtle">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-2">Description</p>
            <Text className="text-text-secondary text-sm">
              {club?.description || 'No description available.'}
            </Text>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1">Contact</p>
              <Text className="text-text-secondary text-sm">
                {club?.contact_email || 'No contact email.'}
              </Text>
            </div>
            {club?.website_url && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1">Website</p>
                <a
                  href={club.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark underline text-sm"
                >
                  {club.website_url}
                </a>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ── 2. Sessions ───────────────────────────────────────── */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-4">
          Sessions
        </p>

        <ClubSessionsOverviewCarousel
          embedded
          maxItems={null}
          upcomingSessions={upcomingSessions}
          recentSessions={recentSessions}
          currentUser={currentUser}
          clubId={clubId}
          canManage={canManage}
          sessionWinRates={sessionWinRates}
          onRefresh={onRefresh}
          onViewSessionPerformance={onViewSessionPerformance}
        />
      </div>

      {/* ── 3. Tournaments ────────────────────────────────────── */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-4">
          Tournaments
        </p>

        {seasons && seasons.length > 0 && (
          <div className="mb-4">
            <label
              htmlFor="club-tab-season-select"
              className="block text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-2"
            >
              Season
            </label>
            <select
              id="club-tab-season-select"
              value={selectedSeason?.id || ''}
              onChange={(e) => {
                const id = e.target.value;
                if (!id) { setSelectedSeason(null); return; }
                const season = seasons.find((s) => s.id === id);
                setSelectedSeason(season || null);
              }}
              className="form-input max-w-xs"
            >
              <option value="">All seasons</option>
              {seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name} ({new Date(season.start_date).getFullYear()})
                </option>
              ))}
            </select>
          </div>
        )}

        <ClubTournamentList
          clubId={clubId}
          canManageTournaments={false}
          seasonId={selectedSeason?.id ?? null}
          layout="embed"
        />
      </div>

    </div>
  );
}
